import { useToastStore } from '@/stores/toast'
import type { PlayerT } from '@/types/User'

export const useHelpers = () => {
  const printEvent = (eventName: string, data?: any, type: 'log' | 'debug' | 'warn' | 'error' = 'log') => {
    if (import.meta.env.PROD) return
    switch (type) {
      case 'debug':
        console.debug(`%c[Socket Event] ${eventName}`, 'color: #6B7280; font-style: italic;', data)
        break
      case 'warn':
        console.warn(`%c[Socket Event] ${eventName}`, 'color: #F59E0B; font-weight: bold;', data)
        break
      case 'error':
        console.error(`%c[Socket Event] ${eventName}`, 'color: #EF4444; font-weight: bold;', data)
        break
      case 'log':
      default:
        console.log(`%c[Socket Event] ${eventName}`, 'color: #10B981; font-weight: bold;', data)
        break
    }
  }

  const sleep = (time: number) => {
    return new Promise(resolve => setTimeout(resolve, time))
  }

  const trimString = (str: string) => str.trim().replace(/\s+/g, ' ')

  const handleCustomError = (error: any, isShowToast = true) => {
    printEvent('Custom Error', error, 'error')
    if (isShowToast) {
      showToast(error || 'An error occurred', 'error', 3000)
    }
  }

  const setCookie = (name: string, value: string, days = 0) => {
    const domain = window.location.hostname

    // If days is not passed, the cookie will be a session cookie
    if (days) {
      const expires = new Date()
      expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000)
      document.cookie = `${name}=${value};domain=${domain};path=/;expires=${expires.toUTCString()};SameSite=None;Secure`
    } else {
      document.cookie = `${name}=${value};domain=${domain};path=/;SameSite=None;Secure`
    }
  }

  const getCookie = (name: string) => {
    const cookies = document.cookie.split(';')
    for (let i = 0; i < cookies.length; i++) {
      const cookie = cookies[i].trim()
      // Check if this cookie starts with the name we're looking for
      if (cookie.startsWith(name + '=')) {
        // Return the cookie value
        return cookie.substring(name.length + 1)
      }
    }
    // If cookie is not found, return null
    return null
  }

  const removeCookie = (name: string) => {
    const domain = window.location.hostname
    document.cookie = `${name}=;domain=${domain};path=/;expires=Thu, 01 Jan 1970 00:00:00 UTC;`
  }

  const sendParentWindowMessage = (eventName: string, data?: any) => {
    // * should be replaced with the actual origin of the parent window
    const payload = { type: eventName, data }

    try {
      if (typeof window.kmpJsBridge?.postMessage === 'function') {
        window.kmpJsBridge.postMessage(JSON.stringify(payload))
        return
      }

      if (window.parent && typeof window.parent.postMessage === 'function') {
        window.parent.postMessage(payload, '*')
      }
    } catch (error) {
      printEvent(`Failed to send parent window message: ${eventName}`, error, 'warn')
    }
  }

  const createDummyPlayer = ({ id, username = '', firstName = '', lastName = '', avatarUrl = '', points = 0, status = 'online', hand = [], type = 'human', school = null, email = null }: PlayerT) => {
    return {
      id,
      username,
      lastName,
      firstName,
      avatarUrl,
      points,
      status,
      hand,
      type,
      school,
      email
    }
  }

  const showToast = (message: string, type: 'success' | 'error' = 'success', time = 3000) => {
    const toastStore = useToastStore()
    toastStore.set({ message, type, time })
  }

  const formatDigitToPosition = (digit: number) => {
    const lastTwoDigits = digit % 100
    if (lastTwoDigits >= 11 && lastTwoDigits <= 13) {
      return `${digit}th`
    }

    const lastDigit = digit % 10
    if (lastDigit === 1) {
      return `${digit}st`
    } else if (lastDigit === 2) {
      return `${digit}nd`
    } else if (lastDigit === 3) {
      return `${digit}rd`
    } else {
      return `${digit}th`
    }
  }

  const formatNumber = (digit: string | number) => {
    const num = parseInt(digit.toString())
    const formatted = new Intl.NumberFormat('en-US', { maximumSignificantDigits: 3 }).format(num)
    return formatted
  }

  const isMobileDevice = () => {
    const mobileDeviceRegex = /iPhone|iPad|iPod|Android/i
    return mobileDeviceRegex.test(navigator.userAgent)
  }

  return {
    showToast,
    printEvent,
    sleep,
    trimString,
    handleCustomError,
    setCookie,
    getCookie,
    removeCookie,
    sendParentWindowMessage,
    createDummyPlayer,
    formatDigitToPosition,
    formatNumber,
    isMobileDevice
  }
}
